import React, { ButtonHTMLAttributes, ReactNode } from "react";
import clsx from "clsx";
import { twMerge } from "tailwind-merge";
import {
  FaArrowRight,
  FaChevronDown,
  FaPencil,
  FaTrashCan,
  FaUserMinus,
  FaUserPlus,
} from "react-icons/fa6";
import {
  HiArrowLeft,
  HiBan,
  HiCalendar,
  HiCheckCircle,
  HiDocumentSearch,
  HiDownload,
  HiEye,
  HiEyeOff,
  HiLink,
  HiOutlineUpload,
  HiPhotograph,
  HiPlus,
  HiPlusCircle,
  HiSearch,
  HiThumbUp,
  HiTrash,
} from "react-icons/hi";
import { Spinner } from "./Spinner";
import {
  BiAtom,
  BiCheck,
  BiCog,
  BiPaperclip,
  BiSave,
  BiShow,
  BiSolidEnvelope,
  BiSolidPlusCircle,
  BiSolidStar,
  BiStar,
  BiTrash,
} from "react-icons/bi";
import { UnlockBadge } from "./UnlockBadge";
import { FaCopy, FaHandsHelping } from "react-icons/fa";

interface CustomButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "title"> {
  variant?:
    | "primary"
    | "secondary"
    | "danger"
    | "warning"
    | "outline"
    | "gray"
    | "none"
    | "custom";
  title?: ReactNode;
  tooltip?: string;
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>["onClick"];
  icon?:
    | "trash"
    | "thumbsUp"
    | "ban"
    | "banCurrent"
    | "plus"
    | "plusCircle"
    | "arrowLeft"
    | "pencil"
    | "pencilWhite"
    | "envelop"
    | "userMinus"
    | "trashRed"
    | "calendar"
    | "calendarCurrent"
    | "arrowRight"
    | "userPlus"
    | "userPlusCurrent"
    | "chevronDown"
    | "solidPlusCircle"
    | "hiTrash"
    | "documentSearch"
    | "search"
    | "starRed"
    | "star"
    | "paperClip"
    | "atom"
    | "show"
    | "download"
    | "eye"
    | "eyeOff"
    | "pencilGray"
    | "tick"
    | "save"
    | "copy"
    | "link"
    | "settings"
    | "handsHelping"
    | "photograph"
    | "upload"
    | "check"
    | ReactNode
    | undefined;
  isLoading?: boolean;
  href?: string;
  comp?: ReactNode;
  showUpgrade?: boolean;
  titleClassName?: string;
  upgradeText?: string;
  size?: CustomButtonSize;
}

export type CustomButtonSize = "xs" | "sm" | "md" | "lg" | "icon";

const baseClasses =
  "flex items-center justify-center gap-2 transition-colors focus:outline-none " +
  "disabled:opacity-50 disabled:cursor-not-allowed " +
  "focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500";

const styledClasses =
  "text-center font-figtree font-medium rounded-md disabled:shadow-none";

const sizeClasses: Record<NonNullable<CustomButtonProps["size"]>, string> = {
  xs: "px-2.5 py-1 min-h-0 text-xs",
  sm: "px-3 py-1.5 min-h-[32px] text-sm",
  md: "px-4 py-2 min-h-[38px] text-sm",
  lg: "px-5 py-2.5 min-h-[44px] text-base",
  icon: "p-1.5 min-h-0",
};

const variantClasses = {
  primary:
    "border border-transparent bg-blue-600 text-white shadow-sm enabled:hover:bg-blue-700 focus-visible:ring-blue-500",
  secondary:
    "border border-gray-300 bg-white text-gray-700 shadow-sm enabled:hover:bg-gray-50 focus-visible:ring-blue-500",
  danger:
    "border border-transparent bg-red-600 text-white shadow-sm enabled:hover:bg-red-700 focus-visible:ring-red-500",
  warning:
    "border border-transparent bg-amber-400 text-amber-950 shadow-sm enabled:hover:bg-amber-500 focus-visible:ring-amber-500",
  outline:
    "border border-blue-600 text-blue-600 enabled:hover:bg-blue-50 focus-visible:ring-blue-500",
  none: "",
  gray: "border border-transparent bg-gray-500 text-white shadow-sm enabled:hover:bg-gray-600 focus-visible:ring-gray-500",
  custom: "border border-transparent shadow-sm",
};

type IconKey = keyof typeof icons;

const icons = {
  trash: <FaTrashCan size={18} className="shrink-0 text-gray-400" />,
  trashRed: <BiTrash size={20} className="shrink-0 text-red-500" />,
  thumbsUp: <HiThumbUp size={20} className="text-green-400" />,
  ban: <HiBan size={20} className="text-red-400" />,
  banCurrent: <HiBan size={20} />,
  plus: <HiPlus size={20} className="self-center" />,
  plusCircle: <HiPlusCircle size={20} className="shrink-0 self-center" />,
  arrowLeft: <HiArrowLeft size={18} className="text-gray-500" />,
  pencil: <FaPencil className="text-gray-700" />,
  pencilWhite: <FaPencil className="text-white" />,
  pencilGray: <FaPencil className="text-gray-400" />,
  envelop: <BiSolidEnvelope size={20} className="shrink-0  self-center" />,
  userMinus: <FaUserMinus size={18} className="shrink-0 text-gray-400" />,
  calendar: <HiCalendar size={20} className="text-gray-700" />,
  calendarCurrent: <HiCalendar size={20} />,
  arrowRight: <FaArrowRight />,
  userPlus: <FaUserPlus size={20} color="white" className="self-center" />,
  userPlusCurrent: <FaUserPlus size={20} className="self-center" />,
  chevronDown: <FaChevronDown size={14} />,
  solidPlusCircle: <BiSolidPlusCircle className="text-white self-center" />,
  hiTrash: <HiTrash size={20} />,
  documentSearch: <HiDocumentSearch color="gray" size={20} />,
  search: <HiSearch size={20} className="shrink-0 self-center" />,
  starRed: <BiSolidStar className="text-red-500" />,
  star: <BiStar size={20} color="black" />,
  paperClip: <BiPaperclip size={20} />,
  atom: <BiAtom size={20} />,
  show: <BiShow size={20} />,
  download: <HiDownload size={20} />,
  eyeOff: <HiEyeOff size={20} className="text-gray-400" />,
  eye: <HiEye size={20} className="text-gray-400" />,
  tick: <BiCheck size={20} />,
  save: <BiSave size={20} />,
  copy: <FaCopy size={20} />,
  link: <HiLink size={20} />,
  settings: <BiCog size={20} />,
  handsHelping: <FaHandsHelping size={20} />,
  photograph: <HiPhotograph size={20} />,
  upload: <HiOutlineUpload size={20} />,
  check: <HiCheckCircle size={20} />,
};

const labelClass = (extra?: string) =>
  twMerge(clsx("flex items-center transition-all duration-200", extra));

export const CustomButton = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  CustomButtonProps
>(
  (
    {
      variant = "secondary",
      size = "md",
      className,
      title,
      tooltip,
      onClick,
      icon,
      isLoading,
      type = "button",
      href,
      comp,
      showUpgrade,
      titleClassName,
      upgradeText,
      ...props
    },
    ref
  ) => {
    const { positionInGroup, ...domProps } = props as any;
    const isUnstyled = variant === "none";
    const sizeClass = isUnstyled
      ? size === "md"
        ? "min-h-[44px] text-sm"
        : sizeClasses[size]
      : sizeClasses[size];
    const lienDesactive = href !== undefined && Boolean(domProps.disabled);

    return href !== undefined ? (
      <a
        href={lienDesactive ? undefined : href}
        aria-disabled={lienDesactive || undefined}
        onClick={
          lienDesactive
            ? (e: React.MouseEvent<HTMLAnchorElement>) => e.preventDefault()
            : (onClick as unknown as React.MouseEventHandler<HTMLAnchorElement>)
        }
        className={twMerge(
          clsx(
            baseClasses,
            !isUnstyled && styledClasses,
            sizeClass,
            variantClasses[variant],
            className,
            "inline-flex no-underline",
            lienDesactive && "pointer-events-none opacity-50"
          )
        )}
        {...domProps}
        title={tooltip}
        ref={ref as React.Ref<HTMLAnchorElement>}
      >
        {comp ? (
          comp
        ) : isLoading ? (
          <span translate="no">
            <Spinner size="sm" />
          </span>
        ) : (
          <>
            {icon && (
              <div translate="no" className="flex">
                {typeof icon === "string" ? icons[icon as IconKey] : icon}
              </div>
            )}
            {showUpgrade ? (
              <div className="flex items-center gap-1.5 min-w-0">
                {title && (
                  <span
                    className={labelClass(clsx("truncate", titleClassName))}
                  >
                    {title}
                  </span>
                )}
                <UnlockBadge customText={upgradeText} className="shrink-0" />
              </div>
            ) : (
              title && (
                <span className={labelClass(titleClassName)}>{title}</span>
              )
            )}
          </>
        )}
      </a>
    ) : (
      <button
        onClick={onClick}
        className={twMerge(
          clsx(
            baseClasses,
            !isUnstyled && styledClasses,
            sizeClass,
            variantClasses[variant],
            className
          )
        )}
        {...domProps}
        title={tooltip}
        type={type}
        ref={ref as React.Ref<HTMLButtonElement>}
      >
        {comp ? (
          comp
        ) : isLoading ? (
          <span translate="no">
            <Spinner size="sm" />
          </span>
        ) : (
          <>
            {icon && (
              <div translate="no" className="flex">
                {typeof icon === "string" ? icons[icon as IconKey] : icon}
              </div>
            )}
            {showUpgrade ? (
              <div className="flex items-center gap-1.5 min-w-0">
                {title && (
                  <span
                    className={labelClass(clsx("truncate", titleClassName))}
                  >
                    {title}
                  </span>
                )}
                <UnlockBadge customText={upgradeText} className="shrink-0" />
              </div>
            ) : (
              title && (
                <span className={labelClass(titleClassName)}>{title}</span>
              )
            )}
          </>
        )}
      </button>
    );
  }
);

CustomButton.displayName = "CustomButton";
